import Router from 'next/router';
import { transferTray } from '../../services/transfer-tray.service';
import { pickUpTransferredFiles } from '../../utils/pickup-transferred-files';
import { consumeUploadAgain, matchRecentFiles, planExpiredRetry, rowHoldsBytes, UPLOAD_AGAIN_EVENT, X2_COPY } from '../../utils/upload-expiry';
import { SessionFilesComponent } from './session-files/session-files.component';
import { RecentOnPageComponent } from './recent-on-page/recent-on-page.component';
import { getToolDisplayName } from '../../utils/filename-utils';
import React, { Component } from 'react';
import { container } from '../../context/module';
import { ReplaySubject, Subject, Subscription, debounceTime, interval } from 'rxjs';
import { PackageType, Packages } from '../../commons/commons';
import ContactUsModal from '../../components/ui/contact-us-modal/contact-us.modal';
import { lazyInject } from '../../context/module';
import { MetricsService } from '../../services/metrics.service';
import { MetricEventName } from '../../types/metric-events';
import { nothingToConvert } from '../../utils/unsupported-input';
import { ConnectivityUtils } from '../../utils/connectivity.utils';
import { DropboxFile } from '../../models/dropbox-file';
import { FileUpload } from '../../models/file-upload';
import { GoogleDriveFile } from '../../models/google-drive-file';
import { Logger } from '../../models/logger';
import { UploadInProgressFile } from '../../models/upload-in-progress-file';
import { EnvironmentService } from '../../services/environment.service';
import { FileManagerService } from '../../services/file-manager.service';
import { FileProcessService } from '../../services/file-process.service';
import { FileUploadService } from '../../services/file-upload.service';
import { LoginService } from '../../services/login.service';
import { SpinnerService } from '../../services/spinner.service';
import { UIContext } from '../../services/ui-context';
import { UploadedFilesService } from '../../services/uploaded-files.service';
import { WebSocketService } from '../../services/websocket.service';
import { WindowService } from '../../services/window.service';
import { FileSelectEvent, FileSelectSource, FileUploadProps } from '../../types/file-upload.types';
import { PageData, RouteProps } from '../../types/route-types';
import { FromType, ProcessType, ToType, WebsocketResponse } from '../../types/types';
import { BrowserUtils } from '../../ui-utils/browser-utils';
import { getExtension } from '../../utils/file';
import { get, isEmpty, isEqual } from '../../utils/lodash-min';
import { reloadAfterExecute } from '../../utils/react.utils';
import { MotiveConfig } from '../../utils/request.utils';
import { RxUtils } from '../../utils/rx-utils';
import { untilDestroyed } from '../../utils/until-destroyed';
import { AdvancedOptionsSectionComponent } from '../ui/advanced-options-section/advanced-options-section.component';
import { ConvertOptionsContextV2, ConvertOptionsSummary } from "../convert-options-v2/convert-options.context";
import { GroupViewState, SelectableOptionViewState } from '../convert-options-v2/convert-options.view-states';
import { applyFeatureFlagGate, FlagValues } from '../convert-options-v2/pdf-compress-presets.gate';
import styles from './file-convert.component.module.scss';
import { FileUploadManager } from './file-upload-manager';
import FileUploadComponent from './file-upload/file-upload.component';
import { ValidationBanner } from '../ui/validation-banner/validation-banner.component';
import { WorkflowSearchComponent } from './workflow-search.component';import { RejectedFile } from '../../services/file-validation.service';
import DropZoneComponent from './drop-zone/drop-zone.component';
// The Studio edges this file is allowed under D10, and no others: a component whose own imports
// are React + an enum + the bound emitter, a gate module that imports NOTHING, the URL helper the
// page base already uses, and `state/studio-events`, whose only runtime imports are the metric
// enum and `utils/metric-events-typed` (which resolves `MetricsService` at CALL time, never at
// decoration time -- `utils/metric-emit.ts:6-20`). Nothing here reaches
// `components/studio/views|media|model` at run time, which is the crash this file's own comment at
// :813-822 records.
import { StudioNudgeComponent } from '../studio/nudge/studio-nudge.component';
import { writeTabParam } from '../../utils/route-state';
import { STUDIO_TAB_ID } from '../studio/studio-gate';
import { emitStudio } from '../studio/state/studio-events';
import type { StillImageNudge } from './file-upload-manager';
// The Frame picker's edges into this file, held to the same D10 shape as the Studio's above:
// `host.ts` is type-only (no runtime code, no React import); the two nudge components import
// React + the metric enum + `utils/metric-events-typed` (call-time resolution) + a stylesheet;
// `state/picker-url.ts` imports only `utils/route-state`, which this file already has;
// `utils/media/media-type-detector.ts` imports a list of extensions. Nothing reaches
// `components/frame-picker/{media,player,frames-grid,…}` at run time — those are the picker
// root's chunk. This component IMPLEMENTS the host contract; the root consumes it (§6.1).
import type { ConvertedEvent, FramePickerHost, StagedFilesSnapshot, StagedRejection } from '../frame-picker/host';
import { PickerNudgeComponent, PickerNudgeOpenSource } from '../frame-picker/nudge/picker-nudge.component';
import { PickOnVideoLinkComponent } from '../frame-picker/nudge/pick-on-video-link.component';
import { openPickerTab } from '../frame-picker/state/picker-url';
import { isVideoExtension } from '../../utils/media/media-type-detector';
// FIX-8 duplicate submissions (UX-SPEC.md section 8). Leaves: the pure comparison, the copy, the
// scheduler-view helper and the one-file download; none reaches the Studio or picker graphs.
import { fileFingerprint } from '../../utils/rerun-canonical';
import { RerunOfferLedger, toolPageRerunView, ToolPageRerunView } from './rerun/rerun-offer';
import { nextRerunRegionId, RERUN_CARD_CHANGE_DEBOUNCE_MS, rerunContext } from './rerun/rerun-context';
import { rerunCopy } from './rerun/rerun-copy';
import { downloadExistingResult } from './rerun/download-existing';
import { PSDate, PSRedableDuration } from '../../utils/date.utils';

interface FileConvertComponentState {
    fileWithMetadataList: UploadInProgressFile<DropboxFile | File | GoogleDriveFile>[],
    fileSelectedByUser: boolean,
    fileSelectBtnClickedByUser: boolean,
    fakeUploadInProgressFileForSelectedFiles: Array<UploadInProgressFile<any>>,
    sideMenuShown: boolean;
    showMainUploadButton: boolean;
    processButtonText?: string;
    rejectedFiles?: RejectedFile[];
    rejectedTotalFiles?: number;
    /** `{files}` while the nudge is on screen; null once accepted, dismissed or declined. */
    studioNudge: { files: number } | null;
    /**
     * The one staged video the Frame picker's nudge is about (FRAME_PICKER_DESIGN.md §6.1), or
     * null: nothing staged, more than one file, not a video, or the offer already spent.
     */
    pickerNudgeFile: UploadInProgressFile<any> | null;
    /** FIX-2 X2-6: "Upload it again" brought this page the card's settings, and they are applied. */
    uploadAgainSettingsKept?: boolean;
    /** FIX-8: does the staged batch repeat a running or a finished run? `none` with the flag off. */
    rerunView?: ToolPageRerunView;
    /** FIX-8 8.4: the "You converted this file…" panel is open (raised by a Convert press only). */
    rerunOfferOpen?: boolean;
}

/**
 * The single-video signal (§6.1 "exactly ONE staged video"), read off the host's own
 * `stagedFiles$` snapshot so the nudge and the picker agree on what is staged. BOTH lists count:
 * the fake placeholder exists before the edge answers, and a second file in either list is a
 * second file. The extension is the test — the page's `acceptedExtensions` already admitted the
 * file, and a video→still page admits nothing but videos, but the check is cheap and keeps the
 * offer honest on a page whose list ever widens.
 */
export function singleStagedVideo(snapshot: StagedFilesSnapshot | null | undefined): UploadInProgressFile<any> | null {
    const real = snapshot?.fileWithMetadataList ?? [];
    const fake = snapshot?.fakeUploadInProgressFileForSelectedFiles ?? [];
    if (real.length + fake.length !== 1) return null;
    const only = real[0] ?? fake[0];
    const name = only?.file?.name;
    if (typeof name !== 'string' || !name) return null;
    return isVideoExtension(getExtension(name)) ? only : null;
}

import { FileProcessEditorComponent, UploadExpiredEvent } from './file-process-editor.component';
import { isPerFileMergeType } from '../../utils/merge-type.utils';
import { mergeUploadInProgressLists } from '../../utils/options.utils';

/**
 * `belowToolAd` is handed in as an element rather than imported here on purpose -- see the
 * comment at its render site.
 *
 * `studioVisible` is the page base's own verdict — `studioTabVisible(pageData, emission)`, the
 * PAGE half and the api-sourced flag together (`converter-page-base.component.tsx:253`). It is a
 * prop rather than a second computation here because the page base is the converter page's only
 * subscriber to `featureFlags$`, and because an offer that disagreed with the tab strip would send
 * people to a tab that is not there. Absent means NO: a caller that does not pass it gets no
 * nudge, which is the safe direction for a surface that is dark on prod on day one.
 *
 * `onStudioHandoff` is how the photos travel (§6.1). The offer is "you picked several photos —
 * make a video of them", and until this existed they did not move: the Studio builds its own
 * `FileUploadManager`, so accepting the nudge opened an EMPTY editor and the user re-uploaded the
 * files they had just uploaded (QA row 8b, final review I-1). It hands over the STAGED
 * `UploadInProgressFile`s rather than the raw `File[]`, because an entry carries
 * `getResponse()`/`getMetadata()` once the edge answers, which is exactly and only what
 * `clipSourceOf` reads — so the editor adopts them as tray rows with no second edge round trip and
 * no second set of FILE_UPLOAD_* events.
 *
 * IN FLIGHT COUNTS. The nudge is raised from `fileSelected` before the upload loop
 * (`file-upload-manager.ts:283`), so on a fast accept nothing is complete yet and an earlier
 * version of this handed over an empty array — the editor opened with an empty tray on exactly
 * the pick the offer was about (re-review N-1). Both of the uploader's lists go, along with its
 * `fileListChanged`, and the editor follows them to completion.
 *
 * Optional, so every other caller of this component is unchanged.
 */
type FileConvertProps = RouteProps & {
    belowToolAd?: any;
    aboveToolAd?: any;
    studioVisible?: boolean;
    /**
     * The page base's verdict on the Frame picker tab (`pickerTabVisible`: the page half AND the
     * api-sourced `featureFlags.framePicker`), exactly as `studioVisible` is for the Studio. Gates
     * the single-video nudge and the "Pick it on the video" link (FRAME_PICKER_DESIGN.md §6.1, D1:
     * flag off hides both). Absent means NO.
     */
    pickerVisible?: boolean;
    /**
     * Round 6 (R6-I5, H6 UX#13): the Frame picker tab is the SELECTED tab. The single-video nudge is
     * an offer to open the picker, so it is never raised (nor counted as NUDGE_SHOWN) while the
     * picker is what the user is looking at -- Quick convert's body is hidden under it.
     */
    pickerActive?: boolean;
    /**
     * The page base's verdict on Compress PDF's flag-gated levels and level copy
     * (`featureFlags.pdfBoxCompressor`, `pdf-compress-presets.gate.ts`). Absent means NO: the two
     * strong levels and the notes stay hidden, which is today's page.
     */
    pdfCompressPresetsEnabled?: boolean;
    /**
     * The page base's verdict on compress-wav's flag-gated Auto codec and its note
     * (`featureFlags.compressNoGainPolicy`, same gate). Absent means NO: PCM 16-bit stays the
     * default with today's note.
     */
    compressNoGainPolicyEnabled?: boolean;
    /**
     * The page base's verdict on `featureFlags.compressWavAutoDefault` (UXR1-01). Auto shows only with this
     * AND `compressNoGainPolicyEnabled`. Absent means NO.
     */
    compressWavAutoDefaultEnabled?: boolean;
    /**
     * The page base's verdict on FIX-8 (`featureFlags.rerunDedupeOffer === true`): "Starting…",
     * "View progress" and the unchanged-re-run panel. Absent means NO: today's Convert button.
     */
    rerunDedupeOffer?: boolean;
    onStudioHandoff?: (handoff: { uploads: any[]; changes?: any; source: string }) => void;
};

/** The option-tree gate's flags from the page base's verdicts (`pdf-compress-presets.gate.ts`). */
function optionTreeFlagsOf(props: FileConvertProps): FlagValues {
    return {
        pdfBoxCompressor: props.pdfCompressPresetsEnabled === true,
        compressNoGainPolicy: props.compressNoGainPolicyEnabled === true,
        compressWavAutoDefault: props.compressWavAutoDefaultEnabled === true,
    };
}

export default class FileConvertComponent extends Component<FileConvertProps, FileConvertComponentState> {
    logger = Logger.getLogger('FileConvertComponent');

    editorRef = React.createRef<FileProcessEditorComponent>();

    private title: string;
    private description: string;
    private advancedOptionsAutoSelectDialogShown = false;
    public packageType: PackageType = null;
    public get isPaidUser() {
        return !!this.packageType;
    }
    public get isProUser() {
        return this.isPaidUser ? this.packageType === Packages.PACKAGE_TYPE_PRO : false;
    }

    public processType: ProcessType;
    public fromType: Array<FromType>;
    public toType: ToType;
    public accept: string;
    public fileUploadBtnText: string = 'Select Files';
    public btnText: string;
    public selectedOutputExtension: any;
    public convertOptionsContextV2: ConvertOptionsContextV2;

    public convertSubscriber$: Subscription;
    private subscribersToClose: Subscription[] = [];
    public startConvertProcessTriggered = false;
    public convertProcessStartRequestProcessed = false;

    private hasFileUploads = false;

    effectiveMotives: MotiveConfig;
    // private fileUploadArray: FileUpload[];

    // @Output() 
    onSidePanelFileUploadsChange = new ReplaySubject<FileUpload[]>();

    fileUploadManager: FileUploadManager;

    // ------------------------------------------------------------------ the picker's host (§6.1)
    //
    // Everything the Frame picker needs from Quick convert, and nothing it does not: the SAME
    // options context (one source of truth — the compact Output panel and Advanced Options render
    // one tree), the SAME staged files (one `FileUploadManager` per page, never a second upload),
    // the SAME request path (`FileProcessEditorComponent.convertFiles`), and the moment a
    // conversion has been scheduled so the picker can record `sid → workflow` before the page
    // navigates. The page base passes `this.host` down as the picker root's `host` prop.

    /**
     * Mirror of `FileUploadManager.fileListChanged`, owned HERE because the manager is built
     * lazily on the first pick (`fileSelected` below) and the picker may subscribe before that.
     * Replay(1): a picker that mounts after files were staged sees them at once. Seeded with
     * two empty lists so a subscriber can render "nothing staged" without a null check.
     */
    private readonly stagedFiles = new ReplaySubject<StagedFilesSnapshot>(1);
    /** Fires once per successful `convertFiles`, BEFORE `Router.push('/downloads')`. */
    private readonly converted = new Subject<ConvertedEvent>();
    /**
     * Round 14 (picker K4 / R13 N3): batches handed to `convertFiles` whose success has not come back yet. The picker
     * converts N pinned frames as N calls in one synchronous loop (one `screenshotTime` per job); the page used to
     * `Router.push('/downloads')` on the FIRST success, and the navigation is a document unload (`pagehide` 10 ms
     * before the second schedule response, traced on dev), so the second job's workflow id never reached the
     * `FRAMEPICKER-<sid>` record -- one id at arrival, at +10 s and after a reload, on QA and on dev; that card had no
     * "Frame at …" until it completed. The page now moves once, when the LAST pending batch has reported.
     */
    private pendingBatches = 0;
    private batchSuccesses = 0;
    /**
     * What the uploader refused (R9b): `FileUploadManager.validationResult`, re-emitted on a
     * component-owned subject for the same reason `stagedFiles` is -- the manager is built lazily
     * on the first pick and the picker may subscribe before that. Not a replay: a refusal from
     * before the picker opened is not news to it.
     */
    private readonly rejections = new Subject<StagedRejection>();

    /**
     * Stage files exactly as a drop on Quick convert would (§6.1 "Files dropped on the tab go
     * through `host.stageFiles(files, 'framepicker')`"): the same validation, the same upload,
     * the same file card, the same FILE_SELECTED event — tagged with `source` so the funnel can
     * tell the two entry points apart.
     */
    public stageFiles = (files: File[], source: FileSelectSource): void => {
        // Round 6 (UX#13): a video staged FROM the picker is the offer accepted -- no nudge for it later.
        if (source === 'framepicker') this.pickerNudgeSpent = true;
        this.fileSelected({ files, source } as FileSelectEvent).catch(console.error);
    };

    /**
     * Convert the given staged files through the editor's ONE request path. A no-op before the
     * editor has mounted — there is nothing staged to convert then either.
     */
    public convertFiles = (files: UploadInProgressFile<any>[]): void => {
        const editor = this.editorRef.current;
        if (!editor) {
            this.logger.warn({ message: 'convertFiles called before the editor mounted', data: { count: files?.length } });
            return;
        }
        // Round 6 (UX#13): the caller is an editor with its own progress screen (the picker), so the
        // page's "Connecting to server" toast stays quiet -- it fired 3× over the picker's progress card.
        this.pendingBatches++;
        editor.convertFiles(files, { quiet: true }).catch((e) => { console.error(e); this.settleBatch(false); });
    };

    /**
     * The host object, built ONCE per component (in the constructor, right after the options
     * context it exposes) so the picker root's `host` prop is referentially stable across the
     * page's renders. A fresh object per render would re-subscribe the picker every keystroke.
     */
    public readonly host: FramePickerHost;

    sortableOptions = {
        onUpdate: (event: any) => {
            this.sortOrderChanged(event);
        },
    };


    wsConnected = true;

    get shouldShowLoading() {
        return this.state.fakeUploadInProgressFileForSelectedFiles.length > 0;
    }

    get getLoadingFileList() {
        return Array.from(this.state.fakeUploadInProgressFileForSelectedFiles);
    }

    @lazyInject(UIContext) private uiContext: UIContext;
    @lazyInject(MetricsService) private metricsService: MetricsService;
    @lazyInject(UploadedFilesService) private uploadedFilesService: UploadedFilesService;
    @lazyInject(WebSocketService) public websocketService: WebSocketService; // Template used this
    @lazyInject(FileProcessService) public fileProcessService: FileProcessService;
    @lazyInject(WindowService) public windowService: WindowService;
    @lazyInject(FileManagerService) private fileManagerService: FileManagerService;
    @lazyInject(SpinnerService) private spinnerService: SpinnerService;
    @lazyInject(EnvironmentService) private environmentService: EnvironmentService;
    @lazyInject(LoginService) private loginService: LoginService;
    @lazyInject(FileUploadService) private fileUploadService: FileUploadService;


    // sensors = useSensors(
    //     useSensor(PointerSensor),
    //     useSensor(KeyboardSensor, {
    //       coordinateGetter: sortableKeyboardCoordinates,
    //     })
    //   );

    readonly state: Readonly<FileConvertComponentState> = {
        fileWithMetadataList: [],
        fileSelectedByUser: false,
        fakeUploadInProgressFileForSelectedFiles: [],
        sideMenuShown: false,
        showMainUploadButton: true,
        fileSelectBtnClickedByUser: false,
        studioNudge: null,
        pickerNudgeFile: null,
    };

    /** One offer per page visit. A second pick after "No thanks" is not a second chance to ask. */
    private nudgeSpent = false;
    /** Same rule for the Frame picker's nudge (§6.1 "once per visit"). */
    private pickerNudgeSpent = false;

    // ----------------------------------------------------------- FIX-8 duplicate submissions
    /** UX-SPEC 8.4 "at most once per file per page visit": a visit is this component's life. */
    private readonly rerunLedger = new RerunOfferLedger();
    /**
     * Set the moment the page decides to leave for /downloads (`settleBatch`). The double press
     * lived in this gap: `startConvertProcess` clears its own flags when the schedule call returns,
     * the button went live again while `/downloads` loaded, and a second press scheduled a second
     * job (1,939 of 5,795 unchanged re-runs came under 10 s apart). A press in this window is
     * dropped with or without the flag -- no new string, just no second job.
     */
    private navigatingToDownloads = false;
    private readonly rerunRegionId = nextRerunRegionId('rerun-convert');
    private mountedForRerun = false;

    private get rerunDedupeOn(): boolean {
        return this.props.rerunDedupeOffer === true;
    }

    /** From the first press until the page leaves or the request fails (UX-SPEC 8.2). */
    private get convertBusy(): boolean {
        // FIX-2 (T3): a silent re-upload after a 410 (X2-5 on the staged row) keeps the press busy too.
        return this.startConvertProcessTriggered || this.convertProcessStartRequestProcessed || this.navigatingToDownloads
            || this.expiredReuploadInFlight;
    }
    constructor(props: RouteProps) {
        super(props);
        const wsStatus = this.websocketService.status.pipe(
            untilDestroyed(this),
        ).subscribe({
            next: () => {
                this.wsConnected = this.websocketService.isConnected() && this.websocketService.isServiceReady();
            }
        });
        this.subscribersToClose.push(wsStatus);

        this.convertOptionsContextV2 = ConvertOptionsContextV2.newContext(
            props.convertOptionsV2
        );
        // Before the first render, so the export and the hydrating client both draw today's levels.
        applyFeatureFlagGate(this.convertOptionsContextV2, optionTreeFlagsOf(props as FileConvertProps));
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        this.host = {
            optionsContext: this.convertOptionsContextV2,
            // R9b: the PAGE's list, read late so it is whatever `pageData` says at the time of the pick.
            get acceptedExtensions(): readonly string[] {
                const list = (self.props.pageData as any)?.acceptedExtensions;
                return Array.isArray(list) ? list.map((e: unknown) => String(e).toLowerCase()) : [];
            },
            rejected$: this.rejections.asObservable(),
            stagedFiles$: this.stagedFiles.asObservable(),
            stageFiles: this.stageFiles,
            convertFiles: this.convertFiles,
            onConverted$: this.converted.asObservable(),
        };
        this.stagedFiles.next({ fakeUploadInProgressFileForSelectedFiles: [], fileWithMetadataList: [] });
        // The picker's nudge follows the SAME feed the picker does (§6.1): no second signal from
        // the uploader, no second definition of "staged". Replay(1) means the seed above arrives
        // synchronously here; it agrees with the initial state, so nothing is set before mount.
        this.stagedFiles.pipe(untilDestroyed(this)).subscribe(this.onStagedFilesForPickerNudge);
        this.convertOptionsContextV2.modelChangeObserver.pipe(untilDestroyed(this)).subscribe(() => {
            const text = this.getProcessButtonText();
            if (text !== this.state.processButtonText) this.setState({ processButtonText: text });
            // FIX-8 8.3/8.4: any option change re-answers "unchanged?" at once -- Convert comes back.
            this.refreshRerunView();
        });

        const userDetailsChangedSub = this.loginService.onUserDetailsChanged.subscribe({
            next: (userDetails) => {
                // avMaxFileLimit / nonAVMaxFileLimit used to be recomputed here per package
                // tier. Nothing ever read them -- not this component, not the upload manager,
                // not the backend (STUDIO_DESIGN.md §1.2 F8, §15.10). They looked like a
                // client-side file ceiling that does not exist, which is worse than no field at
                // all: the real per-tier limits are enforcement policies on the backend.
                this.packageType = userDetails && userDetails.isPaidUser() ? userDetails.packageType : null;
            }
        });
        this.subscribersToClose.push(userDetailsChangedSub);

        // const fileSelectedSub = this.fileUploadService.onFileSelected.subscribe({
        //     next: () => {
        //         this.fileSelectedByUser();
        //     }
        // });
        // this.subscribersToClose.push(fileSelectedSub);

        // const fileSelectBtnClickedSub = this.windowService.onFileSelectBtnClicked.subscribe({
        //     next: () => {
        //         this.fileSelectButtonClicked();
        //     }
        // });
        // this.subscribersToClose.push(fileSelectBtnClickedSub);


        this.processPageData(props.pageData);
    }

    componentWillUnmount() {
        if (typeof window !== 'undefined') {
            window.removeEventListener('pagehide', this.onPageHideAbandonment);
            window.removeEventListener(UPLOAD_AGAIN_EVENT, this.onUploadAgainHere);
        }
        this.subscribersToClose.forEach(sub => {
            RxUtils.safeCloseSubscriber(sub);
        });
        this.fileUploadManager?.dispose();
        // The host dies with the component that owns the context and the uploader (§0
        // "Placement"): a picker still subscribed is told so rather than left on a dead seam.
        this.stagedFiles.complete();
        this.converted.complete();
    }

    /**
     * The measured form of the upload→convert loss (sweep P2): the page went away
     * while files sat staged and Convert was never pressed. sendBeacon survives
     * pagehide, so this is one of the few signals that can be sent from here.
     */
    private onPageHideAbandonment = () => {
        if (this.startConvertProcessTriggered) return;
        const staged = this.state.fileWithMetadataList?.length ?? 0;
        if (staged === 0) return;
        container.get(MetricsService).emit(MetricEventName.UPLOAD_ABANDONED, {
            page: this.props.path || '',
            fileCount: staged,
        });
    };

    private getProcessButtonText(): string {
        if (this.convertOptionsContextV2) {
            const summary = this.convertOptionsContextV2.getSummary() as any;
            if (isPerFileMergeType(summary?.mergeType)) return 'Convert';
        }
        return this.btnText || this.processType || 'Process';
    }

    private processPageData(value: PageData) {
        this.title = value.page_seo.title;
        this.description = value.page_seo.description && value.page_seo.description.replace(/\s+/g, ' ');
        this.processType = value.processType?.toLowerCase() as ProcessType;
        this.fromType = value.fromType?.map((type) => type.toLowerCase()) as FromType[];
        this.toType = value.toType?.toLowerCase() as ToType;
        this.accept = value.accept;
        this.btnText = value.btnText;
        this.fileUploadBtnText = value.file_uload_button_text
    }

    componentDidMount() {
        this.setState({
            fileSelectedByUser: false,
        });
        const fileReloadSub = this.uploadedFilesService.getOnRequestFileReuploadSubscriber().pipe(
            untilDestroyed(this),
        ).subscribe({
            next: (fileUpload: FileUpload) => {
                this.fileSelected({
                    files: fileUpload.file,
                } as any).catch(console.error);
                this.uploadedFilesService.removeFileFromFileList(fileUpload);
                this.forceUpdate();
            },
            error: (err) => {
                this.logger.error({ message: 'Failed to load PageData', error: err });
                this.forceUpdate();
            },
        });
        this.subscribersToClose.push(fileReloadSub);
        this.pickUpTransferredFiles();
        this.pickUpUploadAgain();
        if (typeof window !== 'undefined') {
            window.addEventListener('pagehide', this.onPageHideAbandonment);
            // FIX-2: "Upload it again" pressed in a Re-edit modal opened from this page's Re-run row (same route).
            window.addEventListener(UPLOAD_AGAIN_EVENT, this.onUploadAgainHere);
        }
        // FIX-8: a card appearing or going changes the answer too...
        this.mountedForRerun = true;
        this.fileManagerService?.fileUploadsListChanged?.pipe(untilDestroyed(this)).subscribe({
            next: () => this.refreshRerunView(),
            error: (e) => this.logger.error({ message: 'Re-run check failed', error: e }),
        });
        // ...and so does one finishing or failing. That is a per-card change, which `fileUploadsListChanged` (add and
        // remove only) never reports: "View progress" outlived a failed run, so an unchanged retry -- which runs
        // normally (UX-SPEC 8.5) -- had no Convert key until something else re-rendered (fu-t10 F2). Debounced, as a
        // running card reports progress several times a second.
        this.fileManagerService?.fileUploadChanged?.pipe(debounceTime(RERUN_CARD_CHANGE_DEBOUNCE_MS), untilDestroyed(this)).subscribe({
            next: () => this.refreshRerunView(),
            error: (e) => this.logger.error({ message: 'Re-run check failed', error: e }),
        });
    }

    private pickUpTransferredFiles() {
        pickUpTransferredFiles({
            acceptedExtensions: (this.props.pageData as any)?.acceptedExtensions,
            pageSlug: this.props.path,
            // source: 'handoff' — by the time these bytes reach the uploader they
            // are indistinguishable from a local pick, so the tag is the only way
            // FILE_SELECTED can tell us whether arrive-with-file is being used.
            stage: (files) => { this.fileSelected({ files, source: 'handoff' } as any).catch(console.error); },
        });
    }

    /**
     * FIX-2 (UX-SPEC §2.2): "Upload it again" on an expired card landed here. The one-shot handoff (consumed whatever it
     * says) carries the card's settings: they are applied and X2-6 says so. If this browser still remembers the file
     * (`transfer-tray` session store, 4 h), it is staged too -- rare, that store expires first.
     */
    private pickUpUploadAgain() {
        try {
            const handoff = consumeUploadAgain(this.props.path);
            if (!handoff) return;
            if (Object.keys(handoff.options).length > 0) {
                this.convertOptionsContextV2.applySummary(handoff.options as ConvertOptionsSummary);
                // Same gate as the constructor and the re-edit modal: a carried level that is hidden here is not kept.
                applyFeatureFlagGate(this.convertOptionsContextV2, optionTreeFlagsOf(this.props as FileConvertProps));
                this.setState({ uploadAgainSettingsKept: true });
            }
            transferTray.session()
                .then(recent => {
                    const files = matchRecentFiles(handoff, recent).map(r => r.file);
                    if (files.length > 0) this.fileSelected({ files, source: 'handoff' } as any).catch(console.error);
                })
                .catch(() => { /* best-effort: the user picks the file */ });
        } catch (e) {
            this.logger.warn({ message: 'Upload-again handoff could not be applied', error: e });
        }
    }

    private onUploadAgainHere = () => this.pickUpUploadAgain();

    /** Rows this visit already re-uploaded after a 410: a second refusal of the same row is X2-4, never a loop. */
    private readonly reuploadedAfterExpiry = new WeakSet<object>();
    /** While the silent re-upload runs, Convert presses are ignored: its own schedule follows (UX-SPEC §2.1, "schedule once"). */
    private expiredReuploadInFlight = false;

    /**
     * FIX-2 (UX-SPEC §2.1 T3): Convert was refused with 410 UPLOAD_EXPIRED -- the tab sat open past the upload's life.
     * A row that still holds the user's `File` is uploaded again in place (X2-5 "Uploading again…") and scheduled ONCE;
     * a row that does not (Drive / Dropbox, or a second refusal) becomes `blocked` with X2-4 and is left out of Convert.
     * One `schedule.refused_expired` per refused card. Nothing is deleted, and no dialog is shown.
     */
    async onUploadExpired(event: UploadExpiredEvent): Promise<void> {
        const { retry, blocked } = planExpiredRetry(event.expired.map(e => e.files || []), {
            retried: (r) => this.reuploadedAfterExpiry.has(r),
            hasBytes: (r) => rowHoldsBytes(r),
        });
        for (const refused of event.expired) {
            try {
                container.get(MetricsService).emit(MetricEventName.SCHEDULE_REFUSED_EXPIRED, {
                    surface: 'tool_page', page: this.props.path || '',
                    action: retry.some(rows => rows.includes((refused.files || [])[0])) ? 'reupload' : 'blocked',
                });
            } catch { /* never load-bearing */ }
        }
        blocked.forEach(r => r.setStagedStatus({ code: 'UPLOAD_EXPIRED', kind: 'blocked', text: X2_COPY.stagedBlocked, live: true }));
        // The rows whose cards DID go through are converting (their cards are on /downloads): when the page stays to show
        // X2-4 they must not be offered to Convert again, or a second press schedules a duplicate job. Dropped from the
        // staged list only -- no remote delete, the scheduled job needs those uploads.
        this.dropStagedRows(event.scheduledFiles);
        // This batch is settled without a success: nothing it asked for started (a mixed batch's scheduled cards are on
        // /downloads already; the page moves there after the re-upload's own schedule, or stays to show X2-4).
        this.settleBatch(false);
        if (retry.length === 0 || !this.fileUploadManager) {
            retry.flat().forEach(r => r.setStagedStatus({ code: 'UPLOAD_EXPIRED', kind: 'blocked', text: X2_COPY.stagedBlocked, live: true }));
            this.forceUpdate();
            return;
        }
        // The Convert key reads "Loading..." (its existing busy label) until the re-upload's own schedule is sent.
        this.expiredReuploadInFlight = true;
        this.forceUpdate();
        try {
            const ready: UploadInProgressFile<any>[] = [];
            for (const rows of retry) {
                rows.forEach(r => {
                    this.reuploadedAfterExpiry.add(r);
                    r.setStagedStatus({ code: 'UPLOAD_REUPLOADING', kind: 'info', text: X2_COPY.stagedReuploading, live: true });
                });
                const results = await Promise.all(rows.map(r => this.fileUploadManager.reupload(r)));
                if (results.every(Boolean)) {
                    rows.forEach(r => r.setStagedStatus(null));
                    ready.push(...rows);
                } else {
                    rows.forEach(r => r.setStagedStatus({ code: 'UPLOAD_EXPIRED', kind: 'blocked', text: X2_COPY.stagedBlocked, live: true }));
                }
            }
            if (ready.length > 0) this.convertFiles(ready);
        } finally {
            this.expiredReuploadInFlight = false;
            this.forceUpdate();
        }
    }

    /** Take rows off the editor's staged list (and so the page's and the uploader's, via `onFilesChange`), locally. */
    private dropStagedRows(rows: UploadInProgressFile<any>[] | null | undefined) {
        const editor = this.editorRef.current;
        if (!editor || !rows || rows.length === 0) return;
        const drop = new Set(rows);
        const staged = editor.getFiles() || [];
        if (staged.some(f => drop.has(f))) editor.updateFiles(staged.filter(f => !drop.has(f)));
    }

    /** The page's own subtree, so focus checks never reach outside it. */
    private readonly rootRef = React.createRef<HTMLDivElement>();

    /** UX2-04: whether the Convert key had keyboard focus before this update (it may be removed by it). */
    getSnapshotBeforeUpdate(): boolean {
        if (typeof document === 'undefined') return false;
        const active = document.activeElement as HTMLElement | null;
        return !!active && active.getAttribute?.('data-jkey') === 'process-btn' && !!this.rootRef.current?.contains(active);
    }

    /**
     * FIX-6 (UX2-04): when the last convertible file is refused, Convert is replaced by the rows' own Remove. If Convert
     * had focus, focus would drop to <body>; it moves to the first refused row's Remove instead (or to its status line).
     */
    private keepFocusAfterConvertRemoved() {
        this.focusFirstRefusedRow(() => {
            const root = this.rootRef.current;
            if (!root || root.querySelector('[data-jkey="process-btn"]')) return false;
            const active = document.activeElement;
            return !(active && active !== document.body && root.contains(active));
        });
    }

    /**
     * UX3-03: a press that was held because a row was refused after it (`startConversion` sent nothing) leaves Convert in
     * place with nothing near it saying why. The answer is the refused row's line, so focus goes to its Remove (or the
     * line) -- only when focus is still where the press left it (on Convert, or lost to <body>), never from elsewhere.
     */
    private heldPressFocusPending = false;
    private focusRefusedRowAfterHeldPress() {
        this.focusFirstRefusedRow(() => {
            const root = this.rootRef.current;
            if (!root) return false;
            const active = document.activeElement as HTMLElement | null;
            if (!active || active === document.body) return true;
            return active.getAttribute?.('data-jkey') === 'process-btn' && root.contains(active);
        });
    }

    /** Focus the first refused row's Remove (or its status line) while [shouldMove] still says so. */
    private focusFirstRefusedRow(shouldMove: () => boolean, retry = true) {
        const root = this.rootRef.current;
        if (!root || typeof document === 'undefined') return;
        if (!shouldMove()) return;
        const line = root.querySelector('[data-jkey="staged-status"][data-staged-kind="blocked"]') as HTMLElement | null;
        const target = (line?.querySelector('[data-jkey="staged-status-action"]') as HTMLElement | null) ?? line;
        if (!target) {
            // The row may render its line a tick after the page (it re-renders on its own `changes`).
            if (retry) setTimeout(() => this.focusFirstRefusedRow(shouldMove, false), 0);
            return;
        }
        if (target === line && !line.hasAttribute('tabindex')) line.setAttribute('tabindex', '-1');
        target.focus();
    }

    componentDidUpdate(prevProps: FileConvertProps, prevState: FileConvertComponentState, convertHadFocus?: boolean) {
        if (convertHadFocus === true) this.keepFocusAfterConvertRemoved();
        if (this.heldPressFocusPending) {
            this.heldPressFocusPending = false;
            this.focusRefusedRowAfterHeldPress();
        }
        if ((prevProps.pdfCompressPresetsEnabled !== this.props.pdfCompressPresetsEnabled
            || prevProps.compressNoGainPolicyEnabled !== this.props.compressNoGainPolicyEnabled
            || prevProps.compressWavAutoDefaultEnabled !== this.props.compressWavAutoDefaultEnabled)
            && applyFeatureFlagGate(this.convertOptionsContextV2, optionTreeFlagsOf(this.props))) {
            // The options panel re-renders on a change of its view RESULTS, and a flip usually
            // leaves those alone (Screen stays selected), so the panel is re-rendered from here.
            // The emission is for the summary's readers, in case a hidden choice was selected or
            // the gate gave a displaced one back (compress-wav: Auto again once the flag says on).
            this.forceUpdate();
            this.convertOptionsContextV2.emitModelChanged();
        }
        this.updateUploadedFileExtensions();
        if (prevProps.rerunDedupeOffer !== this.props.rerunDedupeOffer
            || prevState.fileWithMetadataList !== this.state.fileWithMetadataList) {
            this.refreshRerunView();
        }
    }

    private updateUploadedFileExtensions() {
        const extensions = this.state.fileWithMetadataList.map(metadata => getExtension(metadata.file.name).toLocaleLowerCase());
        const uniqueExtensions = Array.from(new Set(extensions));
        const currentExtensions = this.convertOptionsContextV2.getRootProperty('uploadedFileExtensions') as string[];
        if (isEmpty(uniqueExtensions)) {
            this.convertOptionsContextV2.unsetRootProperty('uploadedFileExtensions');
        }
        if (!isEqual(uniqueExtensions, currentExtensions)) {
            this.convertOptionsContextV2.setRootProperty('uploadedFileExtensions', uniqueExtensions);
            this.autoSelectCompressionOptions(uniqueExtensions);
        }
    }

    private autoSelectCompressionOptions(uniqueExtensions: string[]) {
        if (this.props.path.endsWith('compressor')) {
            if (uniqueExtensions.length == 1 && !this.advancedOptionsAutoSelectDialogShown) {
                const extensionGroupViewState = this.convertOptionsContextV2.findViewStateByPropertyName('toExtension');
                if (extensionGroupViewState) {
                    const extensionUpperCase = uniqueExtensions[0].toUpperCase();
                    const extensionNode = this.convertOptionsContextV2.findNode(extensionGroupViewState, (id) => {
                        return id.includes(extensionUpperCase);
                    });

                    if (extensionNode && extensionGroupViewState instanceof GroupViewState && extensionNode instanceof SelectableOptionViewState) {
                        if (extensionGroupViewState.selected != extensionNode) {
                            this.uiContext.showConfirmationWarningDialog(
                                `${extensionUpperCase} File Upload Detected`,
                                `Would you like to auto-select optimized settings for ${extensionUpperCase} files?`,
                                () => {
                                    extensionGroupViewState.updateValue(extensionNode);
                                    this.convertOptionsContextV2.emitModelChanged()
                                },
                            )
                            this.advancedOptionsAutoSelectDialogShown = true;
                        }
                    }
                }
            }
        }
    }

    /**
     * Called when files are added or removed in the editor
     */
    onEditorFilesChange(files: UploadInProgressFile<any>[]) {
        this.setState({
            fileWithMetadataList: files
        }, () => {
            this.updateUploadedFileExtensions();
            this.updateSidePanelFileUploads();
        });

        // Update hasFileUploads for UI state
        this.hasFileUploads = files.length > 0;

        if (this.fileUploadManager) {
            this.fileUploadManager.updateFileList(files);
        }
    }

    private populateTestImageForUpload() {
        this.setState({
            fileSelectedByUser: true,
            fileSelectBtnClickedByUser: true,
        });
        setTimeout(async () => {
            const response = await fetch("https://www.xconvert.com/img/xconvert/desktop/windows/screen2.png");
            // here image is url/location of image
            const blob = await response.blob();
            const file = new File([blob], 'image.png', { type: blob.type });
            const file1 = new File([blob], 'Long image name for testing.png', { type: blob.type });
            const file2 = new File([blob], 'This is another image. Little big longer than the previous.png', { type: blob.type });
            this.fileSelected({ files: [file, file1, file2] });
        }, 1000);
    }

    public async startConvertProcess() {
        if (this.hasProcessUploadedFiles() && this.canProcessUploadedFiles()) {
            if (!this.convertProcessStartRequestProcessed) {
                this.convertProcessStartRequestProcessed = true;
                this.stopConvertPolling();
                const sent = await this.convert().catch((e) => { console.error(e); return undefined; });
                this.convertProcessStartRequestProcessed = false;
                this.startConvertProcessTriggered = false;
                // FIX-8 8.2: a failed start re-enables the button now, not at the next unrelated render.
                // FIX-6 (UX2-01): so does a press that sent nothing (a row was refused after the press and now says
                // why) -- the key must not keep reading "Loading..." over a page that stays.
                // UX3-03: and the press's answer is the refused row, so a keyboard user is taken to it.
                if (sent === false) this.heldPressFocusPending = true;
                if (this.rerunDedupeOn || sent === false) this.forceUpdate();
            }
        }
    }

    private convertTimeoutId: any;
    private lastProgressSnapshot: Map<string, number> = new Map();

    public convertWhenReady() {
        // FIX-2: the silent re-upload after a 410 schedules its rows itself, once.
        if (this.expiredReuploadInFlight) return;
        if (!this.startConvertProcessTriggered) {
            this.convertPressVerdictMark = this.editorRef.current?.unsupportedVerdictMark;
            if (this.hasProcessUploadedFiles()) {
                // Funnel spine (sweep P2): the convert click with its context —
                // how many files, and whether any option knob was touched first.
                const metrics = container.get(MetricsService);
                metrics.emit(MetricEventName.CONVERT_START, {
                    page: this.props.path || '',
                    fileCount: this.state.fileWithMetadataList?.length ?? 0,
                    optionsChanged: metrics.optionChangesThisPage,
                });
                if (this.canProcessUploadedFiles()) {
                    this.startConvertProcessTriggered = true;
                    this.startConvertProcess();
                    // QA3-01 (FIX-8 8.2): "Starting…" / aria-busy from the press itself. `startConvertProcess` has set its
                    // flags synchronously by now; without this render the key kept reading Convert, live, for the whole
                    // schedule round trip (~2.6 s on QA). Flag off renders only when the attempt ends, as before.
                    if (this.rerunDedupeOn) this.forceUpdate();
                    return;
                }
                this.startConvertProcessTriggered = true;
                this.convertSubscriber$ = interval(1000).subscribe({
                    next: () => {
                        this.startConvertProcess();
                        // Track progress snapshots
                        this.state.fileWithMetadataList.forEach(f => {
                            this.lastProgressSnapshot.set(f.uuid, f.progress);
                        });
                    },
                });
                this.scheduleUploadTimeout();
                this.uiContext.successToast(`File ${this.processType} will automatically start when upload complete!`, '', 10000);
            } else {
                this.uiContext.errorToast(`Please select files!`, '', false, false);
            }
        } else {
            if (this.canProcessUploadedFiles()) {
                this.startConvertProcess();
            } else {
                this.uiContext.successToast(`Process will start once all files are uploaded!`, '', 10000);
            }
        }
        this.forceUpdate();
    }

    private scheduleUploadTimeout() {
        this.convertTimeoutId = setTimeout(() => {
            if (!this.startConvertProcessTriggered || this.convertProcessStartRequestProcessed) return;

            // If any file made progress since last snapshot, reschedule
            const hasProgress = this.state.fileWithMetadataList.some(f =>
                !f.getComplete() && !f.error && f.progress > (this.lastProgressSnapshot.get(f.uuid) || 0)
            );
            if (hasProgress) {
                this.scheduleUploadTimeout();
                return;
            }

            this.stopConvertPolling();
            this.startConvertProcessTriggered = false;
            const stuckFiles = this.state.fileWithMetadataList
                .filter(f => !f.getComplete() && !f.error)
                .map(f => (f.file as any)?.name || 'unknown')
                .join(', ');
            if (!stuckFiles) { // All files either completed or errored
                if (this.rerunDedupeOn) this.forceUpdate(); // FIX-8: "Starting…" must not outlive the attempt
                return;
            }

            if (!ConnectivityUtils.isOnline()) {
                this.uiContext.showErrorDialog({
                    title: 'Connection Lost',
                    text: 'You appear to be offline. Please check your connection and try again.',
                    confirmButtonText: 'Retry',
                    successCallback: () => this.convertWhenReady(),
                });
            } else {
                this.uiContext.showErrorDialog({
                    title: 'Upload Timeout',
                    text: `Some files are still uploading: ${stuckFiles}. Please retry uploading them or remove them to continue.`,
                    confirmButtonText: 'Retry',
                    successCallback: () => this.convertWhenReady(),
                });
            }
            this.forceUpdate();
        }, 120000);
    }

    /** Stop the convert polling interval and timeout without resetting state flags. */
    private stopConvertPolling() {
        RxUtils.safeCloseSubscriber(this.convertSubscriber$);
        if (this.convertTimeoutId) { clearTimeout(this.convertTimeoutId); this.convertTimeoutId = null; }
    }

    @reloadAfterExecute()
    private resetProcessOnUploadCompleteProcedure() {
        this.stopConvertPolling();
        this.startConvertProcessTriggered = false;
        this.convertProcessStartRequestProcessed = false;
    }

    public updateProcessProbedDataFn() {
        const that = this;
        return async (probeDuration: number) => {
            // Delegated to FileProcessEditorComponent internally for now
            if (that.editorRef.current && that.editorRef.current.fileProcessService) {
                // We might need to expose a method on editor if specific logic is needed
                // For now, returning void/promise as placeholder if logic is fully internal to editor
            }
        };
    }

    /** Resolves false when the editor sent nothing (see `FileProcessEditorComponent.startConversion`). */
    public async convert(): Promise<boolean | undefined> {
        if (this.editorRef.current) {
            return this.editorRef.current.startConversion({ verdictMark: this.convertPressVerdictMark });
        }
        return undefined;
    }

    /**
     * FIX-6 (UX2-01): the editor's `unsupportedVerdictMark` when the user pressed Convert. A press made while the
     * uploads are still running starts later (the 1 s poll in `convertWhenReady`); a file refused in between must
     * not be dropped without the user seeing its "Can't convert" line.
     */
    private convertPressVerdictMark: number | undefined = undefined;

    hasProcessUploadedFiles() {
        return this.state.fileWithMetadataList.length > 0;
    }

    canProcessUploadedFiles() {
        return this.hasProcessUploadedFiles() && this.state.fileWithMetadataList.every(f => f.getComplete());
    }

    async fileSelected(event: FileSelectEvent) {
        if (!this.fileUploadManager) {
            // IMPORTANT: This need to be re-initialized if moved to SPA
            this.fileUploadManager = new FileUploadManager(
                this.state.fakeUploadInProgressFileForSelectedFiles,
                this.state.fileWithMetadataList,
                this.props,
                this.logger,
                this.fromType,
                this.toType,
                () => {
                    this.forceUpdate();
                },
                () => {
                    this.updateSidePanelFileUploads();
                    this.forceUpdate();
                },
                // A THUNK, not `studioVisible` itself: this manager is built once, here, on the
                // first pick, and the page base's verdict arrives later from `featureFlags$`. A
                // captured boolean would be the answer from before the API spoke, and with the
                // §11.1 dark write in place that answer would keep the nudge's 1 MB-per-file byte
                // sniff running on all 37 image→video pages with the Studio switched off
                // (final review I-2). The gate below in `onStillImageNudge` stays: this one stops
                // the READ, that one stops the OFFER.
                { nudge: () => this.props.studioVisible === true },
            );
            this.fileUploadManager.fileListChanged.subscribe((fileListChangeEvent) => {
                this.hasFileUploads = get(fileListChangeEvent, 'fileWithMetadataList', []).length > 0;
                if (this.editorRef.current) {
                    this.editorRef.current.updateFiles(
                        get(fileListChangeEvent, 'fileWithMetadataList', [])
                    );
                }
                // The picker's view of the staged files (§6.1 `stagedFiles$`): the SAME event,
                // re-emitted on the component-owned subject that exists before the manager does.
                this.stagedFiles.next({
                    fakeUploadInProgressFileForSelectedFiles: get(fileListChangeEvent, 'fakeUploadInProgressFileForSelectedFiles', []),
                    fileWithMetadataList: get(fileListChangeEvent, 'fileWithMetadataList', []),
                });
                // FIX-8 8.3: a file added or removed re-answers "already converting?". The list is
                // mutated in place, so `componentDidUpdate` cannot see this change by reference.
                this.refreshRerunView(get(fileListChangeEvent, 'fileWithMetadataList', []));
                this.forceUpdate();
            });
            this.fileUploadManager.validationResult.subscribe((result) => {
                this.setState({ rejectedFiles: result.rejected, rejectedTotalFiles: result.totalFiles });
                // The picker's copy of the same fact (R9b): names and reasons, never the File objects.
                this.rejections.next({
                    rejected: (result.rejected || []).map((r) => ({ name: r.file?.name ?? '', extension: r.extension, reason: r.reason })),
                    totalFiles: result.totalFiles,
                    notice: result.notice,
                });
            });
            this.fileUploadManager.stillImageNudge.subscribe(this.onStillImageNudge);
        }
        await this.fileUploadManager.fileSelected(event);

        // Remember what they picked so other tools can offer it back (T6.2). Fire-and-forget:
        // a convenience riding along with an upload must never delay or break it.
        try {
            const picked = Array.from((event as any)?.files || []) as File[];
            if (picked.length) {
                transferTray.remember(
                    picked,
                    this.props.path || '',
                    getToolDisplayName(this.props.pageData, this.props.path),
                ).catch(() => { /* best-effort */ });
            }
        } catch { /* best-effort */ }
    }

    sortOrderChanged(e: any) {
        // Handled by Editor
    }

    updateSidePanelFileUploads() {
        // No-op or implementation if needed
    }

    decodeData(wsData: WebsocketResponse) {
        if (wsData.dataType === 'notification') {
            return wsData.data;
        } else if (wsData.dataType === 'ratio') {
            return wsData.data;
        } else if (wsData.dataType === 'percentage') {
            return parseFloat(wsData.data).toFixed(2);
        } else {
            return 'Waiting for server to reply';
        }
    }

    showContactUs(e: MouseEvent) {
        e.preventDefault();
        ContactUsModal.show({});
    }

    getFileConverts() {
        const hasStagedFiles = (this.state.fileWithMetadataList || []).length > 0
            || (this.state.fakeUploadInProgressFileForSelectedFiles || []).length > 0;
        return <div className={`${styles['sortable-container']} container-fluid g-0`}>
            {/*
              * Inside the container, not above it. This element reserves min-height 100px for
              * the staged-file list and that space is empty on a first visit, so a 320x100 unit
              * here costs the uploader nothing -- rendering it above instead moved the drop zone
              * from y=363 to y=483. It gives the room straight back the moment a file is staged.
              */}
            {!hasStagedFiles && this.props.aboveToolAd}
            {/*
              * The gate is asserted TWICE on purpose. The API answer can arrive after the banner
              * is on screen -- `featureFlags$` is a live emission and the page base lowers
              * `studioVisible` whenever it says `studio:false` -- and a banner that outlived its
              * destination would be an offer to nowhere. Taking it away silently is right: it was
              * never a real offer, so it is neither dismissed nor declined.
              */}
            {this.state.studioNudge && this.props.studioVisible === true && <StudioNudgeComponent
                files={this.state.studioNudge.files}
                onAccept={this.onNudgeAccept}
                onDismiss={this.onNudgeDismiss}
            />}
            {/*
              * The Frame picker's single-video nudge (§6.1), gated the same way on the page
              * base's `pickerVisible` — asserted here as well as in the subscriber, for the
              * late-flag reason the Studio's comment above gives. The two nudges never meet:
              * the Studio's fires on image→video pages, this one on video→still pages.
              *
              * `thumbs` is empty for now: the four `duration × k/4` thumbnails are
              * `media/frame-source.ts`'s to decode, and that module belongs to the picker's
              * chunk, not to the upload path's import graph (see the header). The copy lines
              * and "Pick the frame" render without them, by the component's own contract.
              */}
            {this.state.pickerNudgeFile && this.props.pickerVisible === true && this.props.pickerActive !== true && <PickerNudgeComponent
                thumbs={[]}
                onOpen={this.onPickerNudgeOpen}
                onDismiss={this.onPickerNudgeDismiss}
            />}
            <FileProcessEditorComponent
                ref={this.editorRef}
                onConversionStart={this.declineNudgeIfShowing}
                initialFiles={this.state.fileWithMetadataList}
                convertOptionsContext={this.convertOptionsContextV2}
                toType={this.toType}
                fromType={this.fromType as any}
                processType={this.processType}
                pageData={this.props.pageData}
                onFilesChange={(files) => this.onEditorFilesChange(files)}
                onConversionSuccess={(event) => this.onConversionSuccess(event)}
                onUploadExpired={(event) => { this.onUploadExpired(event).catch(e => this.logger.error({ message: 'Expired-upload recovery failed', error: e })); }}
            />
        </div>
    }

    /**
     * Offer the Studio only where the tab strip is offering it too.
     *
     * ONE predicate, and it is the page base's `studioVisible` — `studioTabVisible(pageData,
     * emission)`, the page AND an api-sourced `featureFlags.studio` that is not `false`
     * (`studio-gate.ts:112-115`). Re-deriving the page half here would be a second answer to a
     * question that already has one, and the flag half cannot be derived here at all: this
     * component has no settings subscription, by design (`converter-page-base` is the converter
     * page's only subscriber to `featureFlags$`).
     *
     * This matters on day one rather than in theory. §11.1 step 3 dark-writes
     * `featureFlags.studio=false` on prod BEFORE the web deploy that carries this code, so a
     * banner gated on the page alone would appear for every user who picks two photos on any of
     * the 37 image→video pages, its "Open the Studio" would write `?tab=studio` and render
     * nothing, and NUDGE_SHOWN/NUDGE_ACCEPTED would count it as a live funnel.
     */
    private onStillImageNudge = (nudge: StillImageNudge) => {
        if (this.props.studioVisible !== true) return;
        if (this.nudgeSpent) return;
        this.setState({ studioNudge: nudge });
    };

    private onNudgeAccept = () => {
        this.nudgeSpent = true;
        this.setState({ studioNudge: null });
        // The photos go FIRST, and synchronously: the page base holds them in an instance field,
        // and the tab write below is what makes it render the Studio (§6.1). Handing them over
        // afterwards would race the mount.
        //
        // BOTH LISTS, whatever state they are in. `fileWithMetadataList` holds the real entries
        // and `fakeUploadInProgressFileForSelectedFiles` the placeholders this uploader stages for
        // each picked `File` before the edge has answered — and the nudge is raised before the
        // upload loop, so on a fast accept the second list is the only one with anything in it
        // (re-review N-1). The editor keeps one row per `File` and follows `fileListChanged` until
        // the real entry replaces the fake. The handoff is raised even when both lists are empty,
        // so `STUDIO_OPENED{source:'nudge'}` is recorded whatever the timing was.
        this.props.onStudioHandoff?.({
            uploads: [
                ...(this.state.fileWithMetadataList || []),
                ...(this.state.fakeUploadInProgressFileForSelectedFiles || []),
            ],
            changes: this.fileUploadManager?.fileListChanged,
            source: 'nudge',
        });
        // The page base owns `activeTab` and follows the URL (§6.1), so opening the Studio from
        // here is one write and no cross-component call.
        writeTabParam(STUDIO_TAB_ID);
    };

    private onNudgeDismiss = () => {
        this.nudgeSpent = true;
        this.setState({ studioNudge: null });
    };

    // ------------------------------------------------------------- the Frame picker's nudge (§6.1)

    /**
     * Every `stagedFiles$` emission re-answers "exactly one video?". The offer is raised when the
     * answer turns yes and TAKEN AWAY when it turns no (a second file added, the only file
     * removed) — an offer about a video that is no longer alone would be an offer about nothing.
     * Spent stays spent: after "No thanks" or an accept the answer is null for the visit.
     */
    private onStagedFilesForPickerNudge = (snapshot: StagedFilesSnapshot) => {
        const next = this.pickerNudgeSpent ? null : singleStagedVideo(snapshot);
        if (next !== this.state.pickerNudgeFile) this.setState({ pickerNudgeFile: next });
    };

    /**
     * The user said yes. The tab is opened through the picker's own URL module (the page base
     * owns `activeTab` and follows the URL, §6.1), and the picker root records
     * `FRAMEPICKER_OPENED{source:'nudge'|'nudge_thumb'}` on arrival — this component asks, it does
     * not count. `t` (a thumbnail's time) is not written: `picker-url.ts` never puts the playhead
     * in the URL, and no thumbnails are rendered here yet (see the render site).
     */
    private onPickerNudgeOpen = (source: PickerNudgeOpenSource, _t?: number) => {
        this.pickerNudgeSpent = true;
        this.setState({ pickerNudgeFile: null });
        // IT1 (saas #2): the source rides explicitly -- the nudge notes it too, but the owner must not depend on that order.
        openPickerTab(undefined, source);
    };

    private onPickerNudgeDismiss = () => {
        this.pickerNudgeSpent = true;
        this.setState({ pickerNudgeFile: null });
    };

    /**
     * The user pressed Convert with the offer still on screen — §8's NUDGE_DECLINED.
     *
     * Raised here rather than in the banner because "declined" is a fact about what the user did
     * INSTEAD, and the banner cannot see that. Wired to the editor's `onConversionStart`, which
     * fires before anything is scheduled — `onConversionSuccess` is too late, because by then the
     * page has already navigated to /downloads.
     */
    private declineNudgeIfShowing = () => {
        const nudge = this.state.studioNudge;
        if (!nudge) return;
        this.nudgeSpent = true;
        emitStudio(MetricEventName.NUDGE_DECLINED, { files: nudge.files });
        this.setState({ studioNudge: null });
    };

    /**
     * Every card is with the scheduler. The picker hears it FIRST (`onConverted$`, §6.1: "so the
     * picker records `sid → workflow` before `Router.push('/downloads')`"), then the page moves.
     * `Router.push` is asynchronous, so even a synchronous subscriber has its say before the
     * route changes; a subscriber that throws must not stop the navigation, hence the guard.
     */
    private onConversionSuccess(event: ConvertedEvent) {
        try {
            this.converted.next(event);
        } catch (e) {
            this.logger.error({ message: 'onConverted$ subscriber threw', error: e });
        }
        this.settleBatch(true);
    }

    /**
     * Round 14 (picker K4 / R13 N3): one batch has reported (success or failure). The page moves to /downloads once
     * NOTHING is pending and at least one batch succeeded -- Quick convert's single batch, or the last of the picker's
     * N pinned-frame jobs, whose ids are then all on the record before the document unloads. A batch that fails last
     * still lets the earlier successes through; a burst with no success stays on the page (as before).
     */
    private settleBatch(ok: boolean) {
        this.pendingBatches = Math.max(0, this.pendingBatches - 1);
        if (ok) this.batchSuccesses++;
        if (this.pendingBatches > 0 || this.batchSuccesses === 0) return;
        this.batchSuccesses = 0;
        // FIX-8 8.2: the button stays "Starting…" (and a press is dropped) until the page is gone.
        this.navigatingToDownloads = true;
        this.forceUpdate();
        const leaving = Router.push('/downloads') as unknown;
        if (leaving && typeof (leaving as Promise<boolean>).then === 'function') {
            (leaving as Promise<boolean>).then(
                (moved) => { if (moved === false) this.stayedOnPage(); },
                () => this.stayedOnPage(),
            );
        }
    }

    /** The route change was refused or cancelled: the page is still here, so Convert works again. */
    private stayedOnPage() {
        this.navigatingToDownloads = false;
        if (this.mountedForRerun) this.forceUpdate();
    }

    // ------------------------------------------------------- FIX-8 duplicate submissions (§8)

    private rerunPage(): string {
        return this.props.path || '';
    }

    private emitRerun(event: MetricEventName, attrs: Record<string, string> = {}) {
        try {
            container.get(MetricsService).emit(event, { page: this.rerunPage(), ...attrs });
        } catch { /* telemetry is never load-bearing */ }
    }

    private stagedFingerprints(): (string | null)[] {
        const files = this.editorRef.current?.getFiles() ?? this.state.fileWithMetadataList ?? [];
        return files.filter(f => f).map(f => fileFingerprint(f.file as any));
    }

    /** UX-SPEC 8.1 over the staged batch: what the schedule call would post against this browser's cards. */
    private computeRerunView(staged?: UploadInProgressFile<any>[]): ToolPageRerunView {
        const editor = this.editorRef.current;
        if (!this.rerunDedupeOn || !editor) return { kind: 'none' };
        const files = (staged ?? editor.getFiles() ?? []).filter(f => f);
        if (files.length === 0) return { kind: 'none' };
        try {
            return toolPageRerunView({
                enabled: true,
                pending: () => editor.describeScheduleRequests(files),
                cards: this.fileManagerService?.getFileUploads?.() || [],
                ctx: rerunContext(),
                defaults: this.convertOptionsContextV2.getDefaultSummary(),
            }, files.map(f => fileFingerprint(f.file as any)));
        } catch (e) {
            // A check that throws must never stand between the user and Convert.
            this.logger.error({ message: 'Re-run check threw', error: e });
            return { kind: 'none' };
        }
    }

    /**
     * `staged`: the uploader's list when it is the news (`fileListChanged`) -- the editor's own state
     * may not have caught up with the same emission yet.
     */
    private refreshRerunView = (staged?: UploadInProgressFile<any>[]) => {
        if (!this.mountedForRerun) return;
        const view = this.computeRerunView(staged);
        const prev = this.state.rerunView;
        const same = (prev?.kind ?? 'none') === view.kind && (view.kind !== 'succeeded' || (prev?.kind === 'succeeded'
            && prev.endedAt === view.endedAt && prev.priors.length === view.priors.length
            && prev.priors.every((p, i) => p?.id === view.priors[i]?.id)));
        if (same) return;
        // Not while this page's own press is starting or leaving: the cards it just scheduled match the staged files by
        // construction, and counting them would report every conversion as an unchanged re-run (fu-t10 F2).
        if (view.kind === 'running' && prev?.kind !== 'running' && !this.convertBusy) {
            this.emitRerun(MetricEventName.RERUN_UNCHANGED_DETECTED, { surface: 'tool_page', prev: 'running' });
        }
        this.setState({ rerunView: view, ...(view.kind === 'succeeded' ? {} : { rerunOfferOpen: false }) });
    };

    /**
     * The Convert press (UX-SPEC 8.2-8.4). A press while the first is still starting is dropped
     * (flag off: only in the navigation gap, where today's flags no longer guard it). With the flag
     * on, a batch that repeats a finished run asks once -- the panel -- and the next press converts.
     */
    private onConvertPressed = () => {
        if (this.navigatingToDownloads || (this.rerunDedupeOn && this.convertBusy)) {
            this.emitRerun(MetricEventName.CONVERT_DOUBLE_PRESS_SUPPRESSED, { surface: 'tool_page' });
            return;
        }
        if (this.rerunDedupeOn && this.askBeforeConvert()) return;
        if (this.state.rerunOfferOpen) this.setState({ rerunOfferOpen: false });
        this.convertWhenReady();
    };

    private askBeforeConvert(): boolean {
        const view = this.computeRerunView();
        if (view.kind === 'running') {
            // The button should already read "View progress"; the press found out first.
            this.setState({ rerunView: view });
            return true;
        }
        if (view.kind !== 'succeeded') return false;
        const staged = this.stagedFingerprints();
        if (!this.rerunLedger.isDue(staged)) return false;
        this.rerunLedger.markAsked(staged);
        this.emitRerun(MetricEventName.RERUN_UNCHANGED_DETECTED, { surface: 'tool_page', prev: 'succeeded' });
        this.setState({ rerunView: view, rerunOfferOpen: true });
        return true;
    }

    private onRerunOfferDownload = () => {
        const view = this.state.rerunView;
        this.emitRerun(MetricEventName.RERUN_UNCHANGED_DOWNLOAD_CLICKED, { surface: 'tool_page' });
        const refocus = this.rerunOfferHoldsFocus();
        const priors = view?.kind === 'succeeded' ? view.priors : [];
        // One file: its result, through the card's Download path. Several (or a many-file result):
        // /downloads, where each card's own Download key is.
        if (priors.length === 1 && downloadExistingResult(priors[0])) {
            // UX4-07: the key just pressed goes with the panel; focus moves to Convert, not to <body>.
            this.setState({ rerunOfferOpen: false }, () => { if (refocus) this.focusConvertKey(); });
            return;
        }
        this.setState({ rerunOfferOpen: false });
        Router.push('/downloads');
    };

    private onRerunOfferConvertAgain = () => {
        this.emitRerun(MetricEventName.RERUN_UNCHANGED_RUN_ANYWAY, { surface: 'tool_page' });
        const refocus = this.rerunOfferHoldsFocus();
        // UX4-07: as above -- the Convert key (now "Starting…") takes the focus the panel's key had.
        this.setState({ rerunOfferOpen: false }, () => { if (refocus) this.focusConvertKey(); });
        // The ledger already holds these files, so this press converts.
        this.onConvertPressed();
    };

    /** Focus is on a key in the offer panel (or already lost to <body>), so the panel's unmount would drop it. */
    private rerunOfferHoldsFocus(): boolean {
        if (typeof document === 'undefined') return false;
        const active = document.activeElement as HTMLElement | null;
        if (!active || active === document.body) return true;
        const panel = this.rootRef.current?.querySelector('[data-jkey="rerun-offer"]');
        return !!panel && panel.contains(active);
    }

    private focusConvertKey() {
        const key = this.rootRef.current?.querySelector('[data-jkey="process-btn"], [data-jkey="view-progress-btn"]') as HTMLElement | null;
        try { key?.focus(); } catch { /* focus is best effort */ }
    }

    private viewProgress = () => {
        Router.push('/downloads');
    };

    /**
     * UX-SPEC 8.4 tool-page panel: inside a polite status region that is mounted with the staged
     * files (before any text arrives), so the sentence is announced when a press raises it.
     */
    getRerunOffer(): React.JSX.Element | null {
        if (!this.rerunDedupeOn) return null;
        const view = this.state.rerunView;
        const open = this.state.rerunOfferOpen === true && view?.kind === 'succeeded';
        return <div role="status" aria-live="polite" id={`${this.rerunRegionId}-offer`} data-jkey="rerun-offer-region">
            {open && view?.kind === 'succeeded' && <div className={styles['rerun-offer']} data-jkey="rerun-offer">
                <p className={styles['rerun-offer-text']}>
                    {rerunCopy('X8-8', { time: new PSRedableDuration(PSDate.from(new Date(view.endedAt))).fromNow() })}
                </p>
                <div className={styles['rerun-offer-actions']}>
                    <button type="button" className="btn btn-primary square-edges" data-jkey="rerun-offer-download"
                        onClick={this.onRerunOfferDownload}>{rerunCopy('X8-9')}</button>
                    <button type="button" className={`btn square-edges ${styles['rerun-offer-secondary']}`} data-jkey="rerun-offer-convert"
                        onClick={this.onRerunOfferConvertAgain}>{rerunCopy('X8-10')}</button>
                </div>
            </div>}
        </div>;
    }

    /**
     * THE Convert button (T10 owns its rendering). Flag off: today's button, byte for byte, with the
     * navigation-gap press dropped. Flag on: "Starting…" + aria-disabled + aria-busy from the first press
     * (8.2); "View progress" with its helper while the same run is still going (8.3).
     */
    getConvertButton(): React.JSX.Element {
        const label = this.state.processButtonText || this.getProcessButtonText();
        const icon = <img width={"16px"} height={"16px"}
            className={`color-invert ${styles['process-btn-icon']}`}
            src="/icons/svg/arrow-right-short-shaft.svg"
            alt='Start'
        />;
        const className = `btn btn-lg btn-primary square-edges pull-right w-100 ${styles['process-btn']}`;
        if (!this.rerunDedupeOn) {
            return <button className={className}
                onClick={this.onConvertPressed}
                data-jkey="process-btn">
                {
                    // FIX-2 (T3): the silent re-upload after a 410 is part of the same press.
                    this.startConvertProcessTriggered || this.expiredReuploadInFlight ?
                        'Loading...' : <span>{label}{icon}</span>
                }
            </button>;
        }
        const busy = this.convertBusy;
        const running = !busy && this.state.rerunView?.kind === 'running';
        const helpId = `${this.rerunRegionId}-help`;
        return <>
            {running
                ? <button type="button" className={className} onClick={this.viewProgress}
                    aria-describedby={helpId} data-jkey="view-progress-btn">
                    <span>{rerunCopy('X8-2')}{icon}</span>
                </button>
                : <button className={busy ? `${className} disabled` : className}
                    onClick={this.onConvertPressed}
                    // UX3-03: not the native `disabled` -- Chrome drops a disabled key's focus to <body>, so a keyboard
                    // user lost their place at every press. `onConvertPressed` drops a press while busy (8.2).
                    aria-disabled={busy ? true : undefined}
                    aria-busy={busy ? true : undefined}
                    data-jkey="process-btn">
                    {busy ? rerunCopy('X8-1') : <span>{label}{icon}</span>}
                </button>}
            <div id={helpId} role="status" aria-live="polite" className={styles['rerun-helper']} data-jkey="rerun-helper">
                {running ? rerunCopy('X8-3') : null}
            </div>
        </>;
    }

    private isXConvert() {
        return this.environmentService && this.environmentService.isXConvert();
    }

    private toggleSideMenu() {
        this.setState({
            sideMenuShown: !this.state.sideMenuShown,
        });
    }

    private hideMainUploadButton() {
        if (this.state.showMainUploadButton) {
            this.setState({
                showMainUploadButton: !this.state.showMainUploadButton,
            });

            setTimeout(() => {
                BrowserUtils.runIfWindowExists(() => {
                    if (window.innerWidth < 500) {
                        this.toggleSideMenu();
                    }
                });
            })
        }
    }

    handleDragEnd(event) {
        const { active, over } = event;

        if (active.id !== over.id) {
            //   setItems((items) => {
            //     const oldIndex = items.indexOf(active.id);
            //     const newIndex = items.indexOf(over.id);

            //     return arrayMove(items, oldIndex, newIndex);
            //   });
        }
    }

    /**
     * "Files you used recently" (T6.2/T6.4).
     *
     * Lives here rather than on the page component because picking a file has to go through
     * `fileSelected` — the same path a drop takes — so upload, probing, option defaults and the
     * file card all behave identically whether the file came from the device or the tray.
     */
    /** Rows the re-run section will show, so one shared trigger can count both sections. */
    private reRunCount = 0;

    getSessionFiles() {
        const reRun = <RecentOnPageComponent
            processType={this.props.pageData?.processType}
            toType={this.props.pageData?.toType}
            uniqueProcessType={(this.props.pageData as any)?.uniqueProcessType}
            toolName={getToolDisplayName(this.props.pageData, this.props.path)}
            pageSlug={this.props.path}
            onCount={(n) => { if (n !== this.reRunCount) { this.reRunCount = n; this.forceUpdate(); } }}
        />;
        return <SessionFilesComponent
            reRunSection={reRun}
            reRunCount={this.reRunCount}
            acceptedExtensions={this.props.pageData?.acceptedExtensions || []}
            pageSlug={this.props.path}
            toolName={getToolDisplayName(this.props.pageData, this.props.path)}
            pageData={this.props.pageData}
            stagedKeys={(this.state.fileWithMetadataList || [])
                .map(f => `${f?.file?.name}:${f?.file?.size}`)}
            onPick={(files) => { this.fileSelected({ files, source: 'session' } as any).catch(console.error); }}
        />;
    }

    /**
     * The misrouted-files banner, rendered identically whether or not valid files are staged.
     *
     * It used to be two copies of the same JSX in the two branches of render(), so every change
     * had to be made twice or the two states silently diverged.
     */
    getValidationBanner() {
        if (!(this.state.rejectedFiles?.length > 0)) return null;
        return <ValidationBanner
            rejectedFiles={this.state.rejectedFiles}
            totalFiles={this.state.rejectedTotalFiles || 0}
            toolName={getToolDisplayName(this.props.pageData, this.props.path)}
            fromSlug={this.props.path}
            acceptedExtensions={this.props.pageData?.acceptedExtensions || []}
            // Only fires once the user has removed the last pending row. Dismissing the banner
            // keeps the rows, so it must not reach here.
            onDismiss={() => this.setState({ rejectedFiles: [], rejectedTotalFiles: 0 })}
        />;
    }

    render() {
        const extraPropsV2 = {
            convertOptionsContext: this.convertOptionsContextV2,
            // NOT a plain concat: the fake placeholder lives until upload COMPLETES but
            // the real entry exists from upload START — a raw concat lists every file
            // twice for the whole upload (visible in the target-size notes).
            uploadInProgressFiles: mergeUploadInProgressLists(
                this.state.fakeUploadInProgressFileForSelectedFiles,
                this.state.fileWithMetadataList,
            ),
            onOptionChange: (data: ConvertOptionsSummary) => {
                // Remove following
                const summary = this.convertOptionsContextV2.getSummary();
                console.log(summary);
                if (typeof window !== 'undefined') {
                    const key = Object.entries(summary)
                        .filter(([k]) => k !== 'uploadedFileExtensions')
                        .map(([k, v]) => `${k}=${v}`)
                        .join('_')
                        .replace(/[^a-zA-Z0-9_=\-\.]/g, '-');
                    (window as any).__uniqueSummaries = (window as any).__uniqueSummaries || {};
                    (window as any).__uniqueSummaries[key] = summary;
                }
            },
        };
        const fileUploadProps: FileUploadProps = {
            acceptedFileTypes: this.accept || '*',
            uploadBtnText: this.fileUploadBtnText || 'Add Files',
            fileSelectButtonClicked: () => {
                // Skipped
            },
            fileSelected: (ev) => {
                this.fileSelected(ev).catch(console.error);
                this.hideMainUploadButton();
            },
        };


        if (true) {
            const hasFiles = this.state.fileWithMetadataList.length > 0 ||
                this.state.fakeUploadInProgressFileForSelectedFiles.length > 0;
            // FIX-6 (UX-SPEC section 6): every staged row is one we can't convert -> no Convert at all; each row
            // carries its own Remove instead (a button that would do nothing is worse than none).
            const convertReplacedByRemove = nothingToConvert(
                this.state.fileWithMetadataList, this.state.fakeUploadInProgressFileForSelectedFiles.length);

            return <>
                <div className='mt-2' ref={this.rootRef}>
                    <WorkflowSearchComponent />

                    {
                        this.getFileConverts()
                    }

                    {!hasFiles && (
                        <div className='row' key={'file-convert-drop-zone'}>
                            <div className='col-12'>
                                {this.getValidationBanner()}
                                <DropZoneComponent
                                    acceptedExtensions={this.props.pageData?.acceptedExtensions}
                                    fileUploadProps={fileUploadProps}
                                />
                                {/*
                                  * Immediately under the drop zone, because that is where the
                                  * question "which file?" is asked. It was previously last in
                                  * the tool, below the Advanced Options accordion -- measured on
                                  * QA at 412px wide it rendered at y=1,919 against a drop zone
                                  * at y=363, so it was two screens below the only control it
                                  * relates to. A file picker shortcut nobody scrolls to is not
                                  * a feature.
                                  *
                                  * It arrives after hydration, so content below it shifts. The
                                  * drop zone is above it and does not move, which is the part
                                  * that matters -- the user's target stays put.
                                  */}
                                {this.getSessionFiles()}
                            </div>
                        </div>
                    )}

                    {hasFiles && (
                        <div className='row' key={'file-convert-file-upload-container'}>
                            <div className='col-12'>
                                {this.getValidationBanner()}
                                {/*
                                  * Still here once files are staged. Hiding it after the first
                                  * pick stranded anyone adding a second file from the same
                                  * batch -- the list is how they get at it without another trip
                                  * to the device. Rows already staged say "Added" instead of
                                  * disappearing, so nothing jumps and they can see it went in.
                                  */}
                                {this.getSessionFiles()}
                                <div className="card border-0">
                                    <div className="card-body p-0 bg-pattern">
                                        <div className={`row`}>
                                            <div className='col-sm-12 col-md-5'>
                                                <div className={`${styles['file-upload-btn']}`}>
                                                    <FileUploadComponent {...fileUploadProps} shouldDisplayBtnsAsPrimary={true}></FileUploadComponent>
                                                </div>
                                            </div>
                                            <div className='col-sm-12 col-md-3'></div>
                                            <div className='col-sm-12 col-md-4'>
                                                {this.getRerunOffer()}
                                                {/* FIX-6 (T8): a single unconvertible file -- the row's own Remove is the action, not Convert. */}
                                                {!convertReplacedByRemove && this.getConvertButton()}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/*
                      * Directly after the uploader, before the Advanced Options accordion.
                      *
                      * Passed in rather than imported: MotiveComponent pulls the inversify
                      * container, and adding that edge to this file -- which sits in the upload
                      * path's import graph -- crashed the webpack build with a module-eval
                      * error that tsc and jest both pass. The page owns the ad; this component
                      * owns where it goes.
                      * docs/proposed/AD_PLACEMENT_DESIGN.md 9
                      */}
                    {/*
                      * "Pick it on the video" — directly under the staged files and the Upload/Convert
                      * bar, ABOVE the ad and the Advanced Options accordion (mock EntryTab.png; QA Q12:
                      * under the ad it read as part of the ad). Not inside the options panel, which has
                      * no React insertion point besides eval'd notes (FRAME_PICKER_DESIGN.md §6.1).
                      * Gated exactly like the tab: the page base's `pickerVisible` (D1, flag off hides it).
                      */}
                    {this.props.pickerVisible === true && (
                        <div className="row" data-testid="pick-on-video-link-row">
                            <div className='col-12 mt-3'>
                                <PickOnVideoLinkComponent />
                            </div>
                        </div>
                    )}
                    {this.props.belowToolAd}

                    <div className="row">
                        <div className='col-12 mt-4'>
                            {this.state.uploadAgainSettingsKept && <p className={styles['upload-again-settings']} role="status"
                                data-jkey="upload-again-settings-kept">{X2_COPY.settingsKept}</p>}
                            <AdvancedOptionsSectionComponent
                                convertOptionsContext={this.convertOptionsContextV2}
                                uploadInProgressFiles={extraPropsV2.uploadInProgressFiles}
                                onOptionChange={extraPropsV2.onOptionChange}
                                path={this.props.path}
                            />
                        </div>
                    </div>

                </div>
            </>
        }
    }
}
